import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '../components/utils/toast';
import { useLoading } from '../context/LoadingContext';
import { isVersionConflictError } from '../helpers/versionConflictHelper';

export const useShowVersionConflict = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useCallback(
    (error: unknown, queriesToInvalidate: string[]): boolean => {
      if (!isVersionConflictError(error)) return false;

      showToast('error', t('common:recordChangedTryAgain'));
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey: [queryKey] }),
      );
      return true;
    },
    [t, queryClient],
  );
};

interface IUseVersionConflict {
  fetchLatestVersion: () => Promise<string | undefined>;
  queriesToInvalidate: string[];
}

/**
 * Keeps the form as the user left it after a version conflict: the next save sends the version the
 * server holds now, so it goes through without reopening the record.
 */
export const useVersionConflict = ({
  fetchLatestVersion,
  queriesToInvalidate,
}: IUseVersionConflict) => {
  const { t } = useTranslation();
  const showVersionConflict = useShowVersionConflict();
  const { setIsLoading } = useLoading();
  const [latestVersion, setLatestVersion] = useState<string>();

  const handleVersionConflict = (error: unknown): boolean => {
    if (!showVersionConflict(error, queriesToInvalidate)) return false;

    setIsLoading(true);
    fetchLatestVersion()
      .then((version) => version && setLatestVersion(version))
      .catch(() => showToast('error', t('common:somethingWentWrongTryAgain')))
      .finally(() => setIsLoading(false));
    return true;
  };

  return { latestVersion, handleVersionConflict };
};
